import { z } from "zod";
import { apiListSchema } from "./list";
import { Contacts, ContactsSchema } from "./commons";
import { JSONParseSafe } from "@/lib/utils";

//? DTO
export const CreateHotelDtoSchema = z.object({
  Year: z.number(),
  Name: z.string().min(1),
  Address: z.string().nullish(),
  ZipCode: z.string().nullish(),
  PhoneNumber: z.string().nullish(),
  Contacts: z.string().nullish(),
  SocialReason: z.string().nullish(),
  Vat: z.string().nullish(),
  Iban: z.string().nullish(),
  RegionId: z.number().nullish(),
  Locality: z.string().nullish(),
  Priority: z.string().nullish(),
  Category: z.number().nullish(),
  BikeStorage: z.boolean().nullish(),
  BikeStoragePrice: z.number().nullish(),
  BikeStorageQuantity: z.number().nullish(),
  PaymentAfterInvoice: z.boolean().nullish(),
  KidFriendly: z.boolean().nullish(),
  PetFriendly: z.boolean().nullish(),
  ChildMaxAge: z.number().nullish(),
  HasPicnic: z.boolean().nullish(),
  PicnicPrice: z.number().nullish(),
  CheckInTime: z.string().nullish(),
  CheckOutTime: z.string().nullish(),
  BreakfastTime: z.string().nullish(),
  DinnerAutoScheduling: z.boolean().nullish(),
  HasDinner: z.boolean().nullish(),
  DinnerPrice: z.number().nullish(),
  HasBar: z.boolean().nullish(),
  Group: z.string().nullish(),
  BookingDiscount: z.number().nullish(),
  MessageChainId: z.string().nullish(),
  Notes: z.string().nullish(),
  Observations: z.string().nullish(),
  BarValue: z.string().nullish(),
  Email: z.string().nullish()
});
export type CreateHotelDto = z.infer<typeof CreateHotelDtoSchema>;

export const HotelDtoSchema = CreateHotelDtoSchema.extend({
  Id: z.number(),
});

export type HotelDto = z.infer<typeof HotelDtoSchema>;
export const hotelDtoListSchema = apiListSchema(HotelDtoSchema);
//? Model
const NotesSchema = z.object({
  picnic: z.string().nullish(),
  pet: z.string().nullish(),
  bike: z.string().nullish(),
  dinner: z.string().nullish(),
});

type Notes = z.infer<typeof NotesSchema>;

const ObservationsSchema = z.object({
  internal: z.string().nullish(),
  external: z.string().nullish(),
});

type Observations = z.infer<typeof ObservationsSchema>;

export enum HotelCategory {
  Luxury = "Luxury",
  Historical = "Historical",
  Superior = "Superior",
  Comfort = "Comfort",
  Budget = "Budget",
}

export const HotelPriority = ["A", "B", "C", "D", "E", "F"] as const;

export const hotelCategoryEnumToNumber: Record<HotelCategory, number> = {
  Luxury: 0,
  Historical: 1,
  Superior: 2,
  Comfort: 3,
  Budget: 4,
};

const numToEnum = Object.entries(hotelCategoryEnumToNumber).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {}
) as Record<number, HotelCategory>;

export const HotelCategoriesAsArray = Object.values(HotelCategory);

export const CreateHotelSchema = z.object({
  year: z.number().min(1),
  name: z.string().min(1),
  address: z.string().nullish(),
  zipCode: z.string().nullish(),
  phoneNumber: z.string().nullish(),
  contacts: ContactsSchema,
  email: z.string().nullish(),
  socialReason: z.string().nullish(),
  vat: z.string().nullish(),
  iban: z.string().nullish(),
  regionId: z.number().nullish(),
  locality: z.string().nullish(),
  priority: z.string().nullish(),
  category: z.nativeEnum(HotelCategory).nullish(),
  bikeStorage: z.boolean().nullish(),
  bikeStoragePrice: z.number().nullish(),
  bikeStorageQuantity: z.number().nullish(),
  paymentAfterInvoice: z.boolean().nullish(),
  kidFriendly: z.boolean().nullish(),
  petFriendly: z.boolean().nullish(),
  childMaxAge: z.number().nullish(),
  hasPicnic: z.boolean().nullish(),
  picnicPrice: z.number().nullish(),
  checkInTime: z.string().nullish(),
  checkOutTime: z.string().nullish(),
  breakfastStartTime: z.string().nullish(),
  breakfastEndTime: z.string().nullish(),
  dinnerAutoScheduling: z.boolean().nullish(),
  hasDinner: z.boolean().nullish(),
  dinnerPrice: z.number().nullish(),
  hasBar: z.boolean().nullish(),
  group: z.string().nullish(),
  bookingDiscount: z.number().nullish(),
  messageChainId: z.string().nullish(),
  picnicNotes: z.string().nullish(),
  dinnerNotes: z.string().nullish(),
  bikeNotes: z.string().nullish(),
  petNotes: z.string().nullish(),
  externalObservations: z.string().nullish(),
  internalObservations: z.string().nullish(),
  bar: z.record(z.any()).nullish(),
});

export const HotelSchema = CreateHotelSchema.extend({
  id: z.string().min(1),
});

export type CreateHotel = z.infer<typeof CreateHotelSchema>;

export type Hotel = z.infer<typeof HotelSchema>;

//? Mappers

export function hotelFromDto(dto: HotelDto): Hotel {
  const parsedContacts = dto.Contacts
    ? ContactsSchema.safeParse(JSON.parse(dto.Contacts))
    : undefined;
  let contacts: Contacts = [];
  if (parsedContacts?.success) {
    contacts = parsedContacts.data;
  }
  const [breakfastStartTime, breakfastEndTime] = dto.BreakfastTime
    ? dto.BreakfastTime.split(" | ")
    : [undefined, undefined];

  const parsedNotes = dto.Notes
    ? NotesSchema.safeParse(JSONParseSafe<unknown>(dto.Notes))
    : undefined;

  const notes: Notes | undefined = parsedNotes?.success
    ? parsedNotes.data
    : undefined;

  const parsedObservations = dto.Observations
    ? ObservationsSchema.safeParse(
        JSONParseSafe<Observations>(dto.Observations)
      )
    : undefined;

  const observations: Observations | undefined = parsedObservations?.success
    ? parsedObservations.data
    : undefined;

  return {
    id: dto.Id.toString(),
    year: dto.Year,
    name: dto.Name,
    address: dto.Address,
    zipCode: dto.ZipCode,
    phoneNumber: dto.PhoneNumber,
    contacts,
    socialReason: dto.SocialReason,
    vat: dto.Vat,
    iban: dto.Iban,
    regionId: dto.RegionId,
    locality: dto.Locality,
    priority: dto.Priority,
    category: typeof dto.Category === "number" ? numToEnum[dto.Category] : null,
    bikeStorage: dto.BikeStorage,
    bikeStoragePrice: dto.BikeStoragePrice,
    bikeStorageQuantity: dto.BikeStorageQuantity,
    paymentAfterInvoice: dto.PaymentAfterInvoice,
    kidFriendly: dto.KidFriendly,
    petFriendly: dto.PetFriendly,
    childMaxAge: dto.ChildMaxAge,
    hasPicnic: dto.HasPicnic,
    picnicPrice: dto.PicnicPrice,
    checkInTime: dto.CheckInTime,
    checkOutTime: dto.CheckOutTime,
    breakfastStartTime,
    breakfastEndTime,
    dinnerAutoScheduling: dto.DinnerAutoScheduling,
    hasDinner: dto.HasDinner,
    dinnerPrice: dto.DinnerPrice,
    hasBar: dto.HasBar,
    group: dto.Group,
    bookingDiscount: dto.BookingDiscount,
    messageChainId: dto.MessageChainId,
    picnicNotes: notes?.picnic,
    bikeNotes: notes?.bike,
    dinnerNotes: notes?.dinner,
    petNotes: notes?.pet,
    externalObservations: observations?.external,
    internalObservations: observations?.internal,
    bar: dto.BarValue ? JSON.parse(dto.BarValue) : null,
  };
}

export function hotelToDto(dto: Hotel): HotelDto {
  return {
    Id: Number.parseInt(dto.id),
    ...createHotelToDto(dto),
  };
}

export function createHotelToDto(dto: CreateHotel): CreateHotelDto {
  const breakfastTime = [
    dto.breakfastStartTime ?? "",
    dto.breakfastEndTime ?? "",
  ].join(" | ");

  const notes: Notes = {
    dinner: dto.dinnerNotes,
    picnic: dto.picnicNotes,
    pet: dto.petNotes,
    bike: dto.bikeNotes,
  };

  const observations: Observations = {
    internal: dto.internalObservations,
    external: dto.externalObservations,
  };

  return {
    Address: dto.address,
    BikeStorage: dto.bikeStorage,
    BikeStoragePrice: dto.bikeStoragePrice,
    BikeStorageQuantity: dto.bikeStorageQuantity,
    BookingDiscount: dto.bookingDiscount,
    BreakfastTime: breakfastTime,
    Category:
      typeof dto.category === "string"
        ? hotelCategoryEnumToNumber[dto.category]
        : null,
    CheckInTime: dto.checkInTime,
    CheckOutTime: dto.checkOutTime,
    ChildMaxAge: dto.childMaxAge,
    Contacts: dto.contacts ? JSON.stringify(dto.contacts) : null,
    DinnerAutoScheduling: dto.dinnerAutoScheduling,
    DinnerPrice: dto.dinnerPrice,
    Group: dto.group,
    HasBar: dto.hasBar,
    HasDinner: dto.hasDinner,
    HasPicnic: dto.hasPicnic,
    Iban: dto.iban,
    KidFriendly: dto.kidFriendly,
    Locality: dto.locality,
    MessageChainId: dto.messageChainId,
    Name: dto.name,
    PaymentAfterInvoice: dto.paymentAfterInvoice,
    PetFriendly: dto.petFriendly,
    PhoneNumber: dto.phoneNumber,
    PicnicPrice: dto.picnicPrice,
    Priority: dto.priority,
    RegionId: dto.regionId,
    SocialReason: dto.socialReason,
    Vat: dto.vat,
    Year: dto.year,
    ZipCode: dto.zipCode,
    Notes: JSON.stringify(notes),
    Observations: JSON.stringify(observations),
    BarValue: dto.bar ? JSON.stringify(dto.bar) : null,
    Email : dto.email
  };
}
